import { doc, getDoc, type DocumentData } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_POST_DETAIL_FIELDS,
  parseAnalysisDisplayRules,
  type AnalysisDisplayConfig,
  type AnalysisDisplayRules,
  type PostDetailField,
  type PostDetailFieldEntry,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * M5 `analysisDisplayConfigs/{configId}` 遅延ロード hook (Sprint X2 → Sprint AV で全 rules 展開)。
 *
 * 用途:
 *   - PostDetailModal: ECW field 表示順 (`postDetailFieldsOf`)
 *   - Sprint AV 新: AnalysisMatrixView (matrix 軸) / AnalysisHeatmapView (weight)
 *     / AnalysisNetworkGraphView (force / similarity threshold) が
 *     `matrixRules` / `heatmapRules` / `networkRules` を直接 props から消費。
 *
 * 旧との差分:
 *   - 旧 `_cache` Map を module-level に維持 (open/close で毎回 fetch しない)
 *   - configId なし or fetch 失敗時は config:null を返却 (default 表示)
 *   - error は console に流さない (頻発 admin 未設定運用を考慮)
 *
 * Sprint AV 差分 (後方互換):
 *   - 戻り値に `rules` / `matrixRules` / `mapRules` / `heatmapRules` / `networkRules`
 *     / `aiDialogueSettingsId` を追加。既存 `config` / `loading` は破壊しない。
 *   - `rules` は `parseAnalysisDisplayRules` を経由した safe 値 (config:null 時は空 object)。
 */

const _cache = new Map<string, AnalysisDisplayConfig | null>();

export interface UseAnalysisDisplayConfigResult {
  config: AnalysisDisplayConfig | null;
  loading: boolean;
  /** Sprint AV: parse 済 displayRules 全体 (config 未取得時は空 object)。 */
  rules: AnalysisDisplayRules;
  /**
   * Sprint AV: PostDetailModal 用 field 表示順 (fallback 適用済)。
   * 動的管理拡張: legacy string と PostDetailFieldRef object が混在可能 (`PostDetailFieldEntry[]`)。
   */
  postDetailFields: PostDetailFieldEntry[];
  /** Sprint AV: 各 view 用 sub rule の shortcut (未定義時は undefined)。 */
  matrixRules: AnalysisDisplayRules['matrix'];
  mapRules: AnalysisDisplayRules['map'];
  heatmapRules: AnalysisDisplayRules['heatmap'];
  networkRules: AnalysisDisplayRules['network'];
  /** Task D 動的化: AnalysisScoreView 用 sub rule。 */
  scoreRules: AnalysisDisplayRules['score'];
  aiDialogueSettingsId: string | undefined;
}

export function useAnalysisDisplayConfig(
  configId: string | null | undefined,
): UseAnalysisDisplayConfigResult {
  const [config, setConfig] = useState<AnalysisDisplayConfig | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!configId) {
      setConfig(null);
      setLoading(false);
      return;
    }
    if (_cache.has(configId)) {
      setConfig(_cache.get(configId) ?? null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getDoc(doc(db, 'analysisDisplayConfigs', configId))
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          _cache.set(configId, null);
          setConfig(null);
        } else {
          const data = {
            ...(snap.data() as DocumentData),
            id: configId,
          } as AnalysisDisplayConfig;
          _cache.set(configId, data);
          setConfig(data);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [configId]);

  // Sprint AV: rules 派生 (parse は安価だが memo で再描画安定化)
  const rules = useMemo<AnalysisDisplayRules>(
    () => parseAnalysisDisplayRules(config?.displayRules),
    [config],
  );
  const postDetailFields = useMemo<PostDetailFieldEntry[]>(() => {
    if (rules.postDetailFields && rules.postDetailFields.length > 0) {
      return rules.postDetailFields;
    }
    return [...DEFAULT_POST_DETAIL_FIELDS];
  }, [rules]);

  return {
    config,
    loading,
    rules,
    postDetailFields,
    matrixRules: rules.matrix,
    mapRules: rules.map,
    heatmapRules: rules.heatmap,
    networkRules: rules.network,
    scoreRules: rules.score,
    aiDialogueSettingsId: rules.aiDialogueSettingsId,
  };
}

/**
 * displayRules から PostDetailModal で表示する ECW field 順を取得 (default 順あり)。
 *
 * Sprint AU で `PostDetailField` / `DEFAULT_POST_DETAIL_FIELDS` を shared-types に移動。
 * 本 helper 内は `parseAnalysisDisplayRules` の safe-fallback を通して読む。
 * 後方互換のため `PostDetailField` は再 export を維持 (apps 側 import 元を破壊しない)。
 *
 * Sprint AV 注記:
 *   - 同等 logic を hook 内 `postDetailFields` でも返す。
 *   - PostDetailModal は引き続き本 helper を使い、新 view は hook の getter を使う。
 */
export type { PostDetailField, PostDetailFieldEntry };

/**
 * 動的管理拡張: 戻り値は `PostDetailFieldEntry[]` (legacy string + ref object union)。
 * PostDetailModal の renderer は string / object をそれぞれ判別して表示する。
 */
export function postDetailFieldsOf(config: AnalysisDisplayConfig | null): PostDetailFieldEntry[] {
  const rules = parseAnalysisDisplayRules(config?.displayRules);
  if (rules.postDetailFields && rules.postDetailFields.length > 0) {
    return rules.postDetailFields;
  }
  return [...DEFAULT_POST_DETAIL_FIELDS];
}
